import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  useTheme,
  CircularProgress,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  MailOutline as EmailIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG, API_ENDPOINTS, STORAGE_KEYS, USER_ROLES, type UserRole } from '../helpers/constants';
import { encryptPassword } from '@/utils/crypto';
import { updateCurrentUserProfile } from '../services/authService';
import { getMyCompany } from '../services/companyService';
import PasswordPolicyIndicator from '../components/PasswordPolicyIndicator';
import PasswordMatchIndicator from '../components/PasswordMatchIndicator';
import { validatePasswordRequirements, isPasswordValid } from '../helpers/passwordUtils';
import type { PasswordValidation } from '../helpers/passwordUtils';

interface UserProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  location: string;
  role: string;
  emailVerification: 'Pending' | 'Verified';
  mobileVerification: 'Active' | 'Inactive';
  status: 'Active' | 'Inactive';
}

const Profile: React.FC = () => {
  const { user, token, login, markSessionExpired } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [companyName, setCompanyName] = useState<string>('');
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [profileSaveError, setProfileSaveError] = useState('');
  // Password policy validation for Change Password dialog (same as Register/ResetPassword)
  const [passwordValidation, setPasswordValidation] = useState<PasswordValidation>(() =>
    validatePasswordRequirements('')
  );
  
  // Get theme for proper dark mode support
  const theme = useTheme();

  // Initialize from localStorage loggedInUserData if present
  const stored = (() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();
  const isSsoUser = stored?.login_type === 'sso';
  const defaultName = (stored?.name as string | undefined) || '';
  const [profile, setProfile] = useState<UserProfile>({
    id: (stored?.user_id as string | undefined) || user?.id || '1',
    email: (stored?.email_id as string | undefined) || '',
    firstName: defaultName || '',
    lastName: '',
    phone: '',
    location: '',
    role: (stored?.role as string | undefined) || 'User',
    emailVerification: 'Pending',
    mobileVerification: 'Active',
    status: 'Active',
  });

  // Fetch company name from localStorage or API
  useEffect(() => {
    const fetchCompanyName = async () => {
      try {
        const storedUserData = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
        if (!storedUserData) {
          return;
        }

        const userData = JSON.parse(storedUserData);
        
        // First check if company_name already exists in localStorage
        const existingCompanyName = userData.company_name || userData.companyName;
        if (existingCompanyName) {
          setCompanyName(existingCompanyName);
          return;
        }

        // Check if we have authentication tokens before making API call
        const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
        const authToken = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        if (!refreshToken && !authToken) {
          // No tokens available, skip API call
          return;
        }

        // If company name not found, fetch it from API using /my endpoint
        try {
          const companyResponse = await getMyCompany();

          // Extract company name from response (handle different response formats)
          const fetchedCompanyName = companyResponse.name || companyResponse.data?.name || null;
          
          if (fetchedCompanyName) {
            // Update localStorage with company name
            const updatedUserData = {
              ...userData,
              company_name: fetchedCompanyName,
              companyName: fetchedCompanyName
            };
            localStorage.setItem(STORAGE_KEYS.LOGGED_IN_USER_DATA, JSON.stringify(updatedUserData));
            setCompanyName(fetchedCompanyName);
          }
        } catch (apiError: any) {
          // Only log error if it's not a 401 (unauthorized) - that's expected if user is not logged in
          if (apiError?.message && !apiError.message.includes('401') && !apiError.message.includes('Authentication required')) {
            console.error('Error fetching company name from API:', apiError);
          }
          // Don't set company name if API call fails
        }
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    };

    fetchCompanyName();
  }, []);

  const [editedProfile, setEditedProfile] = useState<UserProfile>(profile);

  const handleEdit = () => {
    setProfileSaveError('');
    setEditedProfile(profile);
    setIsEditing(true);
  };

  /** Persists display name via PUT /user-auth/profile (email is not editable in this API). */
  const handleSave = async () => {
    setProfileSaveError('');
    const trimmedName = editedProfile.firstName.trim();
    if (!trimmedName) {
      setProfileSaveError('Name is required.');
      return;
    }

    const authToken =
      localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) ||
      localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) ||
      token ||
      '';
    if (!authToken) {
      setProfileSaveError('You must be signed in to update your profile.');
      return;
    }

    setIsSavingProfile(true);
    try {
      const data = await updateCurrentUserProfile({ name: trimmedName }, authToken);
      const apiUser = data.user;
      const updatedName = apiUser?.name ?? trimmedName;
      const updatedEmail = apiUser?.email_id ?? editedProfile.email;
      const updatedRole = apiUser?.role ?? profile.role;
      const updatedId = String(apiUser?.user_id ?? apiUser?.id ?? profile.id);

      setProfile((prev) => ({
        ...prev,
        firstName: updatedName,
        email: updatedEmail,
        role: updatedRole,
        id: updatedId,
      }));
      setEditedProfile((prev) => ({
        ...prev,
        firstName: updatedName,
        email: updatedEmail,
        role: updatedRole,
        id: updatedId,
      }));

      const raw = localStorage.getItem(STORAGE_KEYS.LOGGED_IN_USER_DATA);
      if (raw) {
        try {
          const prevStored = JSON.parse(raw) as Record<string, unknown>;
          const merged = {
            ...prevStored,
            name: updatedName,
            full_name: updatedName,
            email_id: updatedEmail,
            role: updatedRole,
            user_id: apiUser?.user_id ?? apiUser?.id ?? prevStored.user_id,
          };
          localStorage.setItem(STORAGE_KEYS.LOGGED_IN_USER_DATA, JSON.stringify(merged));
        } catch {
          // UI already updated; storage merge is best-effort
        }
      }

      const authTokenForContext =
        token || localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) || '';
      if (authTokenForContext && user) {
        login(authTokenForContext, {
          ...user,
          name: updatedName,
          full_name: updatedName,
          email: updatedEmail,
          id: updatedId,
          role: (updatedRole as UserRole) || user.role || USER_ROLES.USER,
        });
      }

      setIsEditing(false);
      setSuccessMessage(data.message || 'Profile updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (e: unknown) {
      const err = e as Error & { status?: number };
      if (err.status === 401) {
        markSessionExpired();
      }
      setProfileSaveError(err.message || 'Failed to update profile.');
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleCancel = () => {
    setProfileSaveError('');
    setEditedProfile(profile);
    setIsEditing(false);
  };

  const handleInputChange = (field: keyof UserProfile, value: string) => {
    setEditedProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getInitials = (fullName: string) => {
    return fullName.charAt(0).toUpperCase();
  };
  const handlePasswordUpdate = async () => {
    // Clear previous messages
    setSuccessMessage('');
    setErrorMessage('');
    // Client-side policy check (same as other password flows)
    if (!isPasswordValid(passwordValidation)) {
      setErrorMessage('New password does not meet the requirements below.');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setErrorMessage('New password and confirm password do not match.');
      return;
    }

    // Validate inputs
    if (!currentPassword || !newPassword || !confirmNewPassword) {
      setErrorMessage('All fields are required.');
      return;
    }
    // Validate password complexity
    const passwordRegex = /^(?=.*[A-Z])(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/;
    if (!passwordRegex.test(newPassword)) {
      setErrorMessage('Password must be at least 8 characters long, contain at least one capital letter, and one special character.');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setErrorMessage('New password and confirm password do not match.');
      return;
    }

    setIsUpdatingPassword(true);
    try {
      const encryptedOldPassword = await encryptPassword(currentPassword);
      const encryptedNewPassword = await encryptPassword(newPassword);
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      const response = await fetch(`${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.USER_AUTH.PASSWORD_CHANGE}`, {
        method: 'PUT',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          confirm_old_password: encryptedOldPassword,
          new_password: encryptedNewPassword,
          confirm_new_password: encryptedNewPassword,
          encrypted: true,
        }),
      });
      const data = await response.json();

      if (response.ok) {
        setSuccessMessage(data.message || 'Password updated successfully!');
        setShowPasswordDialog(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmNewPassword('');
        setPasswordValidation(validatePasswordRequirements(''));
        setShowCurrentPassword(false);
        setShowNewPassword(false);
        setShowConfirmPassword(false);
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setErrorMessage(data.message || 'Failed to update password.');
      }
    } catch (error) {
      setErrorMessage('An error occurred while updating the password.');
    } finally {
      setIsUpdatingPassword(false);
    }
  };
  

  const profileData = [
    { label: 'Name', value: profile.firstName },
    { label: 'Role', value: profile.role },
    { label: 'Email', value: profile.email },
    ...(companyName ? [{ label: 'Company', value: companyName }] : []),
  ];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

             <Box sx={{ width: '100%' }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 3, md: 4 }, 
          alignItems: { xs: 'stretch', md: 'flex-start' },
          width: '100%'
        }}>
          {/* Left Panel - User Summary */}
          <Box sx={{ 
            flexShrink: 0, 
            width: { xs: '100%', md: '300px' },
            order: { xs: 2, md: 1 }
          }}>
            <Paper sx={{ p: { xs: 3, sm: 3 }, height: 'fit-content' }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <Avatar
                  sx={{
                    width: 120,
                    height: 120,
                    fontSize: '2.5rem',
                    bgcolor: 'primary.main',
                  }}
                >
                  {getInitials(profile.firstName)}
                </Avatar>
              </Box>
              
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', textAlign: 'left' }}>
                {profile.firstName}
              </Typography>
              
              {/* Email with Icon */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                <EmailIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
                <Typography variant="body1" color="text.secondary">
                  {profile.email}
                </Typography>
              </Box>
              
              {/* Company Name with Icon */}
              {companyName && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                  <BusinessIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
                  <Typography variant="body1" color="text.secondary">
                    {companyName}
                  </Typography>
                </Box>
              )}
              
              {profile.location && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                  {profile.location}
                </Typography>
              )}

              <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={handleEdit}
                  fullWidth
                >
                  Edit Profile
                </Button>
                
                {isSsoUser ? (
                  <Tooltip title="Password is managed by your Single Sign-On (SSO) provider">
                    <span style={{ width: '100%' }}>
                      <Button
                        variant="outlined"
                        startIcon={<LockIcon />}
                        fullWidth
                        disabled
                      >
                        Change Password
                      </Button>
                    </span>
                  </Tooltip>
                ) : (
                  <Button
                    variant="outlined"
                    startIcon={<LockIcon />}
                    onClick={() => setShowPasswordDialog(true)}
                    fullWidth
                  >
                    Change Password
                  </Button>
                )}
                             </Box>
             </Paper>
           </Box>

          {/* Right Panel - Detailed Information */}
          <Box sx={{ 
            flexGrow: 1, 
            minWidth: 0,
            order: { xs: 1, md: 2 },
            width: '100%'
          }}>
            <Paper sx={{ p: { xs: 2, sm: 3 }, width: '100%' }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Profile Information
              </Typography>
              
              <TableContainer>
                <Table>
                  <TableBody>
                    {profileData.map((row, index) => (
                      <TableRow 
                        key={row.label}
                        sx={{ 
                          backgroundColor: index % 2 === 0 
                            ? theme.palette.mode === 'dark' 
                              ? 'rgba(255, 255, 255, 0.05)' 
                              : 'grey.50'
                            : theme.palette.mode === 'dark' 
                              ? 'rgba(255, 255, 255, 0.02)' 
                              : 'white',
                          '&:hover': { 
                            backgroundColor: theme.palette.mode === 'dark' 
                              ? 'rgba(255, 255, 255, 0.08)' 
                              : 'grey.100' 
                          }
                        }}
                      >
                        <TableCell 
                          component="th" 
                          scope="row" 
                          sx={{ 
                            fontWeight: 'bold',
                            width: '30%',
                            borderBottom: 'none'
                          }}
                        >
                          {row.label}:
                        </TableCell>
                        <TableCell sx={{ borderBottom: 'none' }}>
                          <Typography variant="body2">
                            {row.value}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                             </TableContainer>
             </Paper>
           </Box>
         </Box>
      </Box>

      {/* Edit Profile Dialog */}
      <Dialog 
        open={isEditing} 
        onClose={(event, reason) => {
          if (reason === 'backdropClick') {
            return; // Prevent closing when clicking outside
          }
          handleCancel();
        }} 
        maxWidth="sm" 
        fullWidth
        disableEscapeKeyDown
        sx={{
          zIndex: 9999,
          '& .MuiDialog-paper': {
            // Use theme paper so dark mode matches the rest of the app (was hard-coded white)
            backgroundColor: theme.palette.background.paper,
            backgroundImage: 'none',
            color: theme.palette.text.primary,
          },
        }}
      >
        <DialogTitle sx={{ color: 'text.primary' }}>Edit Profile</DialogTitle>
        <DialogContent sx={{ color: 'text.secondary' }}>
          {profileSaveError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {profileSaveError}
            </Alert>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Full Name"
              value={editedProfile.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Email"
              value={editedProfile.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              margin="normal"
              type="email"
              InputProps={{ readOnly: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} disabled={isSavingProfile}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleSave()}
            disabled={isSavingProfile}
            startIcon={isSavingProfile ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          >
            {isSavingProfile ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog 
        open={showPasswordDialog} 
        onClose={(event, reason) => {
          if (reason === 'backdropClick') {
            return; // Prevent closing when clicking outside
          }
          setShowPasswordDialog(false);
        }} 
        maxWidth="sm" 
        fullWidth
        disableEscapeKeyDown
        sx={{
          zIndex: 9999,
          '& .MuiDialog-paper': {
            backgroundColor: theme.palette.background.paper,
            backgroundImage: 'none',
            color: theme.palette.text.primary,
          },
        }}
      >
        <DialogTitle sx={{ color: 'text.primary' }}>Change Password</DialogTitle>
        <DialogContent sx={{ color: 'text.secondary' }}>
          {/* Error message at the top of the dialog */}
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}
          
          <TextField
            fullWidth
            label="Current Password"
            type={showCurrentPassword ? 'text' : 'password'}
            margin="normal"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    edge="end"
                    size="small"
                  >
                    {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label="New Password"
            type={showNewPassword ? 'text' : 'password'}
            margin="normal"
            value={newPassword}
            onChange={(e) => {
              const value = e.target.value;
              setNewPassword(value);
              setPasswordValidation(validatePasswordRequirements(value));
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    edge="end"
                    size="small"
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {/* Password policy indicator (same as forgot/reset/signup/register) */}
          <PasswordPolicyIndicator
            validation={passwordValidation}
            show={!!newPassword}
          />
          <TextField
            fullWidth
            label="Confirm New Password"
            type={showConfirmPassword ? 'text' : 'password'}
            margin="normal"
            value={confirmNewPassword}
            onChange={(e) => setConfirmNewPassword(e.target.value)}
            error={confirmNewPassword.length > 0 && newPassword !== confirmNewPassword}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    size="small"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {/* Password match indicator (same as company signup / reset password) */}
          <PasswordMatchIndicator
            match={newPassword === confirmNewPassword}
            show={!!confirmNewPassword}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setShowPasswordDialog(false);
              setCurrentPassword('');
              setNewPassword('');
              setConfirmNewPassword('');
              setErrorMessage('');
              setPasswordValidation(validatePasswordRequirements(''));
              setShowCurrentPassword(false);
              setShowNewPassword(false);
              setShowConfirmPassword(false);
            }}
            disabled={isUpdatingPassword}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handlePasswordUpdate}
            disabled={isUpdatingPassword}
            startIcon={isUpdatingPassword ? <CircularProgress size={16} /> : null}
          >
            {isUpdatingPassword ? 'Updating...' : 'Update Password'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Profile; 